import { BackendFetch, Tolgee } from '@tolgee/vue'
import { FormatIcu } from '@tolgee/format-icu'
import { dateDeDE, dateEnGB, dateFrFR, deDE, frFR } from 'naive-ui'
import { nextTick, ref } from 'vue'

export const supportedLanguages: Array<TLanguage> = [
  {
    label: 'English',
    value: 'en',
  },
  {
    label: 'Deutsch',
    value: 'de',
  },
  {
    label: 'Français',
    value: 'fr',
  },
]

export const defaultLanguage: TLanguage['value'] = 'en'

// live (in-context) translations are only pulled while developing and only when a Tolgee key is configured
const shouldUseLiveTranslations =
  (import.meta.env.MODE === 'development' || import.meta.env.MODE === 'localapi') &&
  Boolean(import.meta.env.CAPP_TOLGEE_API_KEY)

function getBrowserLanguage() {
  if (navigator.languages !== undefined) return navigator.languages[0]

  return navigator.language
}

export const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
export const browserLanguage = getBrowserLanguage()

export const tolgee = Tolgee()
  .use(shouldUseLiveTranslations ? BackendFetch({ fallbackOnFail: true, timeout: 1000 }) : undefined)
  .use(FormatIcu())
  .init({
    availableLanguages: supportedLanguages.map((lang) => lang.value),
    language: defaultLanguage,
    onTranslationMissing: (info) => {
      // this is needed, otherwise it runs even before Tolgee is initialized
      if (!tolgee.isLoaded() || !info.key) return info.key

      console.warn(`[i18n]: Missing translation for key: ${info.key} | lang: ${tolgee.getLanguage()}`)

      return info.key
    },

    // NOTE: these should NEVER be provided on stage or production
    apiUrl: shouldUseLiveTranslations ? import.meta.env.CAPP_TOLGEE_API_URL : undefined,
    apiKey: shouldUseLiveTranslations ? import.meta.env.CAPP_TOLGEE_API_KEY : undefined,

    staticData: {
      en: () => import('@/assets/locales/en.json'),
      de: () => import('@/assets/locales/de.json'),
      fr: () => import('@/assets/locales/fr.json'),
    },
  })

// include the in-context editing tools everywhere except stage and production
if (shouldUseLiveTranslations) {
  // eslint-disable-next-line node/file-extension-in-import
  import('@tolgee/web/tools').then((module) => {
    tolgee.addPlugin(module.InContextTools())
  })
}

export const naiveLocale = ref<{ locale: typeof deDE; dateLocale: typeof dateDeDE }>()

// load Naive UI translations & date time locales depending on i18n
function setNaiveLocale(locale: TLanguage['value']) {
  if (locale === 'de') naiveLocale.value = { locale: deDE, dateLocale: dateDeDE }
  else if (locale === 'fr') naiveLocale.value = { locale: frFR, dateLocale: dateFrFR }
  else naiveLocale.value = undefined
}

export async function setLanguage(locale: TLanguage['value'] = defaultLanguage) {
  if (!supportedLanguages.some((lang) => lang.value === locale)) {
    console.error(`[i18n]: Unsupported language: ${locale}`)

    return undefined
  }

  // return if the incoming language value is the same
  if (tolgee.getLanguage() === locale) return undefined

  await tolgee.changeLanguage(locale)

  setNaiveLocale(locale)
  localStorage.setItem('language', locale)
  document.querySelector('html')?.setAttribute('lang', locale)

  return nextTick()
}

// Used because we don't want a different language for date-fns than for the rest of the app
export function getLocaleForDateFns() {
  const locale = tolgee.getLanguage() as TLanguage['value']

  const locales = {
    en: dateEnGB,
    de: dateDeDE,
    fr: dateFrFR,
  }

  return locales[locale] ?? dateEnGB
}
